import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import * as yup from 'yup';
import { FormAutocomplete, FormTextField } from '../../../common/form';
import { ContactDto, CustomerDto, CustomerType, ExternalPricelistDto } from '../../../common/PricePrgClient';
import { ProgressButton } from '../../../common/ProgressButton';
import { RootState } from '../../../store';
import { getExternalPricelists } from '../../externalpricelist/redux/externalPricelistSelectors';
import { createCustomer, showCreateCustomerDialog } from '../redux/customerSlices';
import { getAppSettings } from '../../settings/redux/settingsSelectors';

const mapStateToProps = (state: RootState) => ({
    externalPricelists: getExternalPricelists(state),
    contacts: state.contacts.contacts,
    existingCustomers: state.customers.customers,
    parentCustomerId: state.customers.ui.createCustomerParentCustomerId,
    languages: getAppSettings(state).availableLanguages
});

type CreateCustomerForm = Omit<
    CustomerDto,
    | 'customerId'
    | 'hasFreightCost'
    | 'calculation'
    | 'customerEmails'
    | 'parentCustomer'
    | 'pricelistMailAttachmentTypes'
    | 'hasPricelistMailWeekdayRestrictions'
    | 'allowedPricelistMailWeekdays'
    | 'responsible'
    | 'erpLanguage'
    | 'latestPricelistMailSendoutResult'
    | 'pricelistMailLayoutType'
>;

const defaultValues = {
    name: '',
    customerNumber: undefined,
    city: '',
    customerType: CustomerType.NormalCustomer,
    externalPricelistId: undefined,
    parentCustomerId: undefined,
    language: 'GB',
    showValidToOnPricelistMail: false
} as CreateCustomerForm;

const readablePricelistName = (extPricelist: ExternalPricelistDto) => {
    return `${extPricelist?.externalPricelistRef} - ${extPricelist?.name}`;
};

type Props = ReturnType<typeof mapStateToProps>;

const CreateCustomerDialog = ({
    externalPricelists,
    parentCustomerId,
    contacts,
    existingCustomers,
    languages
}: Props) => {
    const dispatch = useDispatch();
    const [isSending, setIsSending] = useState(false);

    const schema: yup.SchemaOf<CreateCustomerForm> = yup.object().shape({
        name: yup.string().required().max(60).label('Name'),
        customerNumber: yup
            .number()
            .notOneOf(
                Object.values(existingCustomers).map(c => c.customerNumber),
                'Customer number is taken.'
            )
            .optional()
            .label('Description'),
        parentCustomerId: yup.number().optional().label('Parent customer id'),
        customerType: yup.number().required().label('Customer type'),
        externalPricelistId: yup.number().optional().label('External pricelist'),
        city: yup.string().required().label('City'),
        contactId: yup.number().optional().label('Responsible'),
        language: yup.string().required().oneOf(languages).label('Language'),
        showValidToOnPricelistMail: yup.boolean().required().label('Show valid to on pricelists')
    });

    const { reset, handleSubmit, control } = useForm<CreateCustomerForm>({
        resolver: yupResolver(schema),
        defaultValues: { ...defaultValues, parentCustomerId }
    });
    const [contactList, setContactList] = useState([] as ContactDto[]);

    useEffect(() => {
        reset({ ...defaultValues, parentCustomerId: parentCustomerId });
    }, [reset, parentCustomerId]);

    const submitForm = (form: CreateCustomerForm) => {
        setIsSending(true);
        dispatch(createCustomer.request({ ...form, parentCustomerId: parentCustomerId }));
    };

    useEffect(() => {
        setContactList(Object.values(contacts));
    }, [contacts]);

    const handleClose = () => {
        dispatch(showCreateCustomerDialog(false));
    };

    return (
        <Dialog open={true} maxWidth="lg">
            <DialogTitle>Create new customer</DialogTitle>
            <DialogContent>
                <form id="mail-form" onSubmit={handleSubmit(submitForm)}>
                    <Grid container direction="row" spacing={2}>
                        <Grid item xs={12}>
                            <FormTextField
                                sx={{ my: 1 }}
                                label="Customer number"
                                control={control}
                                name="customerNumber"
                                variant="standard"
                                autoComplete="off"
                                fullWidth
                                inputProps={{}}
                            />
                        </Grid>
                    </Grid>
                    <Grid container direction="row" spacing={2}>
                        <Grid item xs={12}>
                            <FormTextField
                                sx={{ my: 1 }}
                                name="name"
                                fullWidth
                                control={control}
                                label="Name"
                                variant="standard"
                            />
                        </Grid>
                    </Grid>
                    <Grid container direction="row" sx={{ mt: 1 }}>
                        <Grid item xs={12} md={12}>
                            <FormAutocomplete
                                sx={{ my: 1 }}
                                name="externalPricelistId"
                                control={control}
                                defaultValue={null}
                                label="External Pricelist"
                                getOptionLabel={pricelist => {
                                    if (!pricelist) {
                                        return '';
                                    }

                                    return readablePricelistName(externalPricelists[pricelist]);
                                }}
                                isOptionEqualToValue={(option, value) => option === value}
                                options={Object.values(externalPricelists).map(c => c.externalPricelistId)}
                                clearOnEscape
                                style={{ width: 300 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <FormTextField
                                sx={{ my: 1 }}
                                name="city"
                                control={control}
                                fullWidth
                                label="City"
                                variant="standard"
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <FormAutocomplete
                                sx={{ my: 1 }}
                                control={control}
                                name="contactId"
                                label="Responsible"
                                options={contactList.map(c => c.contactId)}
                                getOptionLabel={c => contacts[c].fullName}
                                defaultValue={null}
                            />
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <FormAutocomplete
                                sx={{ my: 1 }}
                                control={control}
                                name="language"
                                label="Language"
                                options={languages}
                            />
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <ProgressButton color="primary" type="submit" form="mail-form" isLoading={isSending}>
                    Send
                </ProgressButton>
            </DialogActions>
        </Dialog>
    );
};

export default connect(mapStateToProps)(CreateCustomerDialog);
